import clsx from "clsx";
import { useDispatch, useSelector } from "react-redux";

import { selectFocusedEvent } from "../../../store/slices/socket/selectors";
import { setModalState } from "../../../store/slices/view";
import type { RootState } from "../../../store/store";

type ScoreComponentProps = {
  rowReverse?: boolean;
  team?: string | null;
  score: number | undefined | null;
};

const ScoreComponent = ({ rowReverse, team, score }: ScoreComponentProps) => {
  return (
    <div
      className={`flex items-center gap-1.5 ${rowReverse ? "flex-row-reverse" : "flex-row"}`}
    >
      <div className="flex h-[50px] w-[50px] items-center justify-center rounded bg-white shadow-sm">
        <span className="font-semibold text-lg text-gray-900">{score}</span>
      </div>
      <span className="font-semibold text-md px-3 text-gray-900">{team}</span>
    </div>
  );
};

/**
 * Triangle SVG used for inning half indicator.
 * Points up for "top", rotate 180deg for "bottom".
 */
const Triangle = ({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) => (
  <svg
    className={className}
    style={{ height: "8px", ...style }}
    viewBox="0 0 14 6"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M7 0L13.0622 5.25H0.937822L7 0Z" fill="currentColor" />
  </svg>
);

type GameStateDisplayProps = {
  inning: { integer: number; half: string };
  outs: number;
  count: { balls: number; strikes: number };
  onClick: () => void;
};

const GameStateDisplay = ({
  inning,
  outs,
  count,
  onClick,
}: GameStateDisplayProps) => {
  const safeOuts = Math.min(Math.max(outs, 0), 2);

  return (
    <button
      type="button"
      className={clsx(
        // Size
        "h-[50px] min-w-[140px]",
        // Layout
        "flex flex-row items-center justify-around",
        // Spacing
        "px-4",
        // Border
        "rounded",
        // Background
        "bg-white",
        // Effects
        "transition-colors",
        // States
        "cursor-pointer hover:bg-[hsla(218,100%,52%,0.05)]",
      )}
      onClick={onClick}
    >
      {/* Inning with top/bottom arrow */}
      <div className="flex h-[50px] flex-col items-center justify-center">
        <div className="flex h-[33%] items-center">
          {inning.half === "top" && (
            <Triangle className="text-gray-900" style={{ marginBottom: -14 }} />
          )}
        </div>
        <span className="font-semibold text-base text-gray-900">
          {inning.integer}
        </span>
        <div className="flex h-[33%] items-center">
          {inning.half === "bottom" && (
            <Triangle
              className="text-gray-900"
              style={{ marginTop: -14, transform: "rotate(180deg)" }}
            />
          )}
        </div>
      </div>

      {/* Outs dots */}
      <div className="flex items-center">
        {[...Array(safeOuts)].map((_e, i) => (
          <div key={i} className="p-0.5">
            <div className="h-2.5 w-2.5 rounded-full bg-black" />
          </div>
        ))}
        {[...Array(2 - safeOuts)].map((_e, i) => (
          <div key={i} className="p-0.5">
            <div className="h-2.5 w-2.5 rounded-full bg-gray-300" />
          </div>
        ))}
      </div>

      {/* Count */}
      <span className="font-semibold text-base text-gray-900">
        {count.balls}-{count.strikes}
      </span>
    </button>
  );
};

export const GameHeader = () => {
  const dispatch = useDispatch();
  const rosters = useSelector((state: RootState) => state.game.rosters);
  const gameState = useSelector((state: RootState) => state.game.gameState);
  const { isViewingPreviousEvent } = useSelector(
    (state: RootState) => state.socket,
  );
  const focusedEvent = useSelector(selectFocusedEvent);
  const focusGameState = focusedEvent?.gameState;

  const handleClick = () => {
    dispatch(setModalState({ state: "Game State Editor", data: {} }));
  };

  const { awayScore, homeScore } = gameState;

  // When viewing a previous event, show that event's game state
  const displayState =
    isViewingPreviousEvent && focusGameState
      ? {
          inning: focusGameState.inning,
          outs: focusGameState.outs,
          count: focusGameState.count,
        }
      : {
          inning: gameState.inning,
          outs: gameState.outs,
          count: gameState.count,
        };

  return (
    <div className="flex w-full flex-row items-center justify-around">
      <ScoreComponent rowReverse team={rosters.away.name} score={awayScore} />
      <GameStateDisplay
        inning={displayState.inning}
        outs={displayState.outs}
        count={displayState.count}
        onClick={handleClick}
      />
      <ScoreComponent team={rosters.home.name} score={homeScore} />
    </div>
  );
};
